import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import { xeroApi } from "../../backend/api/xero";
import type { XeroAccess, XeroConnection, XeroCapabilityDefinition } from "../../backend/api/xero";
import { DataView } from "../../components/patterns/data-view";
import { ErrorState } from "../../components/patterns/states";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { formatDateTime } from "./shared";

type ManagedCapability = XeroCapabilityDefinition & { access_key: "invoices" | "timesheets" };
const accessLabels: Record<XeroAccess, string> = { none: "Disabled (saved)", read: "Read only", write: "Read & write" };

export default function XeroConnectionAccess({ connection, configured, capabilities, loading = false, onSaved, onError }: {
  connection?: XeroConnection;
  configured: boolean;
  capabilities: XeroCapabilityDefinition[];
  loading?: boolean;
  onSaved: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const editable = capabilities.filter((row): row is ManagedCapability => row.access_key !== null);
  const invoiceDefault = editable.find(row => row.access_key === "invoices")?.default_access ?? "none";
  const timesheetDefault = editable.find(row => row.access_key === "timesheets")?.default_access ?? "none";
  const [invoices, setInvoices] = useState<XeroAccess>(connection?.invoice_access ?? invoiceDefault);
  const [timesheets, setTimesheets] = useState<XeroAccess>(connection?.timesheet_access ?? timesheetDefault);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  useEffect(() => {
    setInvoices(connection?.invoice_access ?? invoiceDefault);
    setTimesheets(connection?.timesheet_access ?? timesheetDefault);
  }, [connection?.tenant_id, connection?.invoice_access, connection?.timesheet_access, invoiceDefault, timesheetDefault]);
  const dirty = Boolean(connection && (invoices !== connection.invoice_access || timesheets !== connection.timesheet_access));
  useEffect(() => {
    if (!dirty) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const connect = async () => {
    setBusy(true);
    setNotice("");
    try {
      const result = await xeroApi.connect({ invoices, timesheets });
      if (result.error || !result.data) { onError(result.error?.message ?? "Could not start Xero authorisation."); return; }
      window.location.assign(result.data.authorize_url);
    } catch { onError("Could not start Xero authorisation. Please retry."); }
    finally { setBusy(false); }
  };
  const save = async () => {
    if (!connection) return;
    setBusy(true);
    setNotice("");
    try {
      const result = await xeroApi.saveConnectionAccess(connection.tenant_id, { invoices, timesheets });
      if (result.error || !result.data) { onError(result.error?.message ?? "Could not save portal access."); return; }
      await onSaved();
      setNotice("Portal access saved.");
    } catch { onError("Could not save portal access. Your changes are ready to retry."); }
    finally { setBusy(false); }
  };
  const disconnect = async () => {
    if (!connection) return;
    setBusy(true);
    try {
      const result = await xeroApi.disconnect(connection.tenant_id);
      if (result.error || !result.data) { onError(result.error?.message ?? "Could not confirm revocation."); return; }
      setConfirmDisconnect(false);
      await onSaved();
    } catch { onError("Could not confirm Xero revocation. Please retry."); }
    finally { setBusy(false); }
  };
  const accessCell = (row: ManagedCapability) => {
    const invoice = row.access_key === "invoices";
    return <Select value={invoice ? invoices : timesheets} disabled={busy} onValueChange={(value: XeroAccess) => {
      setNotice("");
      if (invoice) setInvoices(value); else setTimesheets(value);
    }}>
      <SelectTrigger aria-label={`${row.name} access`} className="min-w-40"><SelectValue>{(invoice ? invoices : timesheets) === "none" ? "Disabled (saved)" : undefined}</SelectValue></SelectTrigger>
      <SelectContent>
        {row.allowed_access.map(level => <SelectItem key={level} value={level}>{accessLabels[level]}</SelectItem>)}
      </SelectContent>
    </Select>;
  };
  if (!loading && editable.length === 0) return <ErrorState title="Access settings unavailable" description="The integration API did not return its access controls. Reload to try again." />;
  return <div className="flex flex-col gap-4">
    <DataView caption="Xero integration capabilities" density="compact" rows={editable} status={loading ? "loading" : "ready"} getRowKey={row => row.id}
      columns={[
        { id: "data", header: "Data", primary: true, cell: row => <span className="font-medium">{row.name}</span> },
        { id: "read", header: "Read from Xero", cell: row => <span className="text-sm">{row.read_description}</span> },
        { id: "write", header: "Write to Xero", cell: row => row.write_description ? <span className="text-sm">{row.write_description}</span> : <span className="text-sm text-muted-foreground">Not supported</span> },
        { id: "access", header: "Portal access", cell: accessCell, className: "w-48" },
      ]} />
    <p className="text-xs text-muted-foreground">Read &amp; write includes reads. The integration API validates saved access against Xero’s actual grants. Payroll runs and payments stay in Xero.</p>
    {connection && <details className="text-sm text-muted-foreground"><summary className="cursor-pointer">Xero authorisation details</summary><p className="mt-2">
      Xero grants: invoices {connection.granted_invoice_access}; timesheets {connection.granted_timesheet_access}.
      {connection.status === "connected" && (connection.access_token_expired ? " The access token will refresh on next use." : ` Access token valid until ${formatDateTime(connection.expires_at)}.`)}
      {connection.status !== "connected" && " Authorisation needs attention before operations can resume."}
      {" "}Portal access cannot exceed Xero's granted permissions. Update Xero permissions to renew consent, then save any access changes after returning. Existing access is preserved during consent.
    </p></details>}
    <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-4">
      <p role="status" className="text-sm text-muted-foreground">{notice || (connection ? dirty ? "Unsaved changes" : "Saved connection access" : "New connection choices — authorise to apply")}</p>
      <div className="flex flex-wrap gap-2">
        {connection && connection.status !== "disconnected" && <>
          {dirty && <Button variant="ghost" disabled={busy} onClick={() => { setInvoices(connection.invoice_access); setTimesheets(connection.timesheet_access); setNotice(""); }}>Discard changes</Button>}
          <Button disabled={busy || !dirty} onClick={save}>{busy ? "Saving..." : "Save portal access"}</Button>
        </>}
        <Button variant="outline" disabled={busy || loading || editable.length === 0 || !configured || (invoices === "none" && timesheets === "none")} onClick={connect}>
          <RefreshCw className="h-4 w-4" />{connection ? connection.status === "reconnect_required" || connection.status === "disconnected" ? "Reconnect Xero" : "Update Xero permissions" : "Continue to Xero"}
        </Button>
        {connection && connection.status !== "disconnected" && <Button variant="outline" disabled={busy} onClick={() => setConfirmDisconnect(true)}>Disconnect</Button>}
      </div>
    </div>
    <Dialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}><DialogContent>
      <DialogHeader><DialogTitle>Revoke Xero authorisation?</DialogTitle><DialogDescription>This revokes the Xero grant and disconnects every organisation sharing it. Portal records and mappings remain. If Xero cannot confirm revocation, the connection stays recorded for retry.</DialogDescription></DialogHeader>
      <DialogFooter><Button variant="outline" disabled={busy} onClick={() => setConfirmDisconnect(false)}>Cancel</Button><Button disabled={busy} onClick={disconnect}>{busy ? "Disconnecting..." : "Revoke and disconnect"}</Button></DialogFooter>
    </DialogContent></Dialog>
  </div>;
}
